/**
 * "Why is this record being changed?" — 21 CFR 11.10(e), Phase 0.3.
 *
 * The regulation asks the audit trail to record the reason for a change, and
 * a reason is only worth recording if it is collected at the moment of the
 * change, from the person making it. So this is a modal in the save path, not
 * a field on a form somebody fills in whenever.
 *
 * **It is driven by the server's refusal, never by a client-side rule.**
 * `executor._require_reason` refuses a change that needs one with a
 * `REASON_REQUIRED` validation failure; this catches that, asks, and retries.
 * A client that decided for itself when to ask would be a second copy of the
 * rule, and the copy that drifts is always the one that stops asking.
 *
 * Deliberately the same shape as `WarningsConfirmModal` / `useWarningConfirm`,
 * because it is the same shape of problem: a save that cannot complete until a
 * person answers something. One pattern for both means a surface that already
 * handles warnings handles this by adding one line.
 */

import React, { useCallback, useState } from 'react'
import { createPortal } from 'react-dom'
import { tw } from '../testHooks'

/** What the object offers as a starting point, when it offers anything. */
export interface ReasonOption {
  code: string
  label: string
}

export interface ReasonForChangeModalProps {
  /** Optional picklist. Free text is always allowed beside it. */
  options?: ReasonOption[]
  /** What the server said, so the person knows which change is being asked about. */
  message?: string
  onCancel: () => void
  onProceed: (reason: string, reasonCode: string) => void
}

export function ReasonForChangeModal({
  options = [], message, onCancel, onProceed,
}: ReasonForChangeModalProps) {
  const [code, setCode] = useState('')
  const [text, setText] = useState('')

  // A code alone is a reason; free text alone is a reason; neither is not.
  // The server checks the same thing, so this only saves a round trip.
  const ready = !!(code || text.trim())
  const submit = () => {
    if (!ready) return
    const label = options.find((o) => o.code === code)?.label || ''
    onProceed([label, text.trim()].filter(Boolean).join(' — '), code)
  }

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Reason for change"
      onKeyDown={(e) => {
        if (e.key === 'Escape') onCancel()
      }}
      style={{
        position: 'fixed', inset: 0, zIndex: 1000,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: 'rgba(5,8,13,0.5)',
      }}
    >
      <div style={{
        width: 440, maxWidth: 'calc(100vw - 32px)', borderRadius: 10,
        overflow: 'hidden', background: 'var(--color-bg, #fff)',
        color: 'var(--color-text, #111)',
        boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
      }}>
        <div style={{ padding: '14px 18px 10px',
          borderBottom: '1px solid var(--color-border, #e4e7ec)' }}>
          <div style={{ fontSize: 16, fontWeight: 700 }}>Reason for change</div>
          <p style={{ margin: '4px 0 0', fontSize: 13, lineHeight: 1.55,
            color: 'var(--color-text-secondary, #667085)' }}>
            {message
              || 'This record is under change control. Say why it is being '
                 + 'changed — it is recorded with the change.'}
          </p>
        </div>

        <div style={{ padding: '14px 18px' }}>
          {options.length > 0 && (
            <select
              value={code}
              onChange={(e) => setCode(e.target.value)}
              style={{
                width: '100%', padding: '8px 10px', borderRadius: 6,
                fontSize: 14, marginBottom: 10,
                border: '1px solid var(--color-border, #d0d5dd)',
                background: 'var(--color-bg, #fff)',
                color: 'var(--color-text, #111)',
              }}
            >
              <option value="">— choose a reason —</option>
              {options.map((o) => (
                <option key={o.code} value={o.code}>{o.label}</option>
              ))}
            </select>
          )}
          <textarea
            value={text}
            autoFocus
            rows={3}
            placeholder={options.length
              ? 'Anything else worth recording (optional)'
              : 'Why is this being changed?'}
            onChange={(e) => setText(e.target.value)}
            style={{
              width: '100%', padding: '8px 10px', borderRadius: 6,
              fontSize: 14, resize: 'vertical',
              border: '1px solid var(--color-border, #d0d5dd)',
              background: 'var(--color-bg, #fff)',
              color: 'var(--color-text, #111)',
            }}
          />
          <p style={{ margin: '8px 0 0', fontSize: 13, lineHeight: 1.5,
            color: 'var(--color-text-secondary, #667085)' }}>
            Recorded with the change and part of the audit trail. It cannot be
            added or altered afterwards.
          </p>
        </div>

        <div style={{
          display: 'flex', gap: 8, justifyContent: 'flex-end',
          padding: '12px 18px',
          borderTop: '1px solid var(--color-border, #e4e7ec)',
        }}>
          <button
            type="button"
            onClick={onCancel}
            {...tw('action', 'cancel')}
            style={{
              padding: '6px 14px', fontSize: '0.8625rem', borderRadius: 6,
              border: '1px solid var(--color-border, #ccc)',
              background: 'transparent', cursor: 'pointer',
              color: 'var(--color-text, #111)',
            }}
          >Cancel</button>
          <button
            type="button"
            onClick={submit}
            {...tw('action', 'confirm')}
            disabled={!ready}
            style={{
              padding: '6px 14px', fontSize: '0.8625rem', borderRadius: 6,
              border: 'none', cursor: ready ? 'pointer' : 'not-allowed',
              background: ready ? 'var(--color-accent, #0061D0)'
                : 'var(--color-bg-secondary, #eee)',
              color: ready ? '#fff' : 'var(--color-text-secondary, #888)',
            }}
          >Save with this reason</button>
        </div>
      </div>
    </div>,
    document.body,
  )
}

interface PendingReason {
  message: string
  options: ReasonOption[]
  resolve: (answer: { reason: string; reasonCode: string } | null) => void
}

/**
 * The stateful half, exactly like `useWarningConfirm`.
 *
 *   const { promptReason, reasonModal } = useReasonPrompt()
 *   await attemptWithReason(
 *     (reason, code) => save({ ...body, reason, reason_code: code }),
 *     promptReason,
 *   )
 *   …
 *   {reasonModal}
 */
export function useReasonPrompt(): {
  promptReason: (message: string, options?: ReasonOption[])
    => Promise<{ reason: string; reasonCode: string } | null>
  reasonModal: React.ReactNode
} {
  const [pending, setPending] = useState<PendingReason | null>(null)

  const promptReason = useCallback(
    (message: string, options: ReasonOption[] = []) =>
      new Promise<{ reason: string; reasonCode: string } | null>((resolve) => {
        setPending({ message, options, resolve })
      }), [])

  const settle = (answer: { reason: string; reasonCode: string } | null) => {
    setPending((p) => {
      p?.resolve(answer)
      return null
    })
  }

  const reasonModal = pending ? (
    <ReasonForChangeModal
      message={pending.message}
      options={pending.options}
      onCancel={() => settle(null)}
      onProceed={(reason, reasonCode) => settle({ reason, reasonCode })}
    />
  ) : null

  return { promptReason, reasonModal }
}
